/* eslint-disable @typescript-eslint/no-explicit-any */
import * as queryString from 'query-string'
import { parseCookies } from 'nookies'
import { ApplicationFormAnswers } from '../../components/ApplicationForm/ApplicationFormContext'
import { AddressHistoryState } from '../../components/Questions/CardlessAddressHistoryQuestion/AddressHistoryContext'
import { EmploymentHistoryState } from '../../components/Questions/CardlessEmploymentHistoryQuestion/EmploymentHistoryContext'
import MarketingPreferencesField from '../../components/Questions/MarketingPreferencesField'
import PersonalDetailsAnswers from '../../components/Questions/PersonalDetailsQuestion/PersonalDetailsAnswers'
import QuestionKey from '../../Questions/question-key'
import { VehicleDetailsDataObject } from '../../services/vehicle-service'
import { AddressField, mapAddressEntryToFormAddress } from '../AddressMapper'
import mapToDateOfBirth from '../DateOfBirthMapper'
import { DrivingLicence, mapToDrivingLicence } from '../DrivingLicenceMapper'
import mapToEmploymentForm, { EmploymentField } from '../EmploymentMapper'
import { Gender, mapToGender } from '../GenderMapper'
import { mapToMaritalStatus, MaritalStatus } from '../MaritalStatusMapper'
import { mapToPhone, AppFormPhone } from '../PhoneMapper'
import { mapToTitle, Title } from '../TitleMapper'
import { mapToVehicleDetails, VehicleDetails } from '../VehicleDetailsMapper'
import { mapToVehicleType, VehicleType } from '../VehicleTypeMapper'
import { ParsedUrlQuery } from 'node:querystring'

interface BasicDetails {
  genderField: Gender
  titleField: Title
  forenameField: string
  surnameField: string
  emailField: string
  phoneNumberField: AppFormPhone[],
  dateOfBirthField: string
}

interface AdditionalDetails {
  maritalStatusField: MaritalStatus
  licenceTypeField: DrivingLicence
  validUKPassportFieldSpecified?: boolean
}

interface MarketingPreferences {
  phoneField: true
  emailField: boolean
  smsField: boolean
  thirdPartyReferralField: false
  thirdPartyReferralFieldSpecified: true
}

interface Applicant {
  basicDetailsField: BasicDetails
  additionalDetailsField: AdditionalDetails
  applicantAddressField: AddressField[]
  applicantEmploymentField: EmploymentField[]
  marketingPreferencesField: MarketingPreferences
}

interface GeneralDetails {
  creditLimitField: number
  vehicleTypeField: VehicleType
}

export interface Metadata {
  isBotTraffic: boolean,
  isOfficeTraffic: boolean,
  customerId: string,
  partnerCustomerId?: string,
  deviceId: string,
  sessionId: string,
  lender: string | undefined,
  isPrefill: boolean,
  isPreapproved: boolean | undefined,
  isRetry: boolean,
  experiments: string,
  journey: string,
  wbraid?: string,
  gbraid?: string,
  fbclid?: string
}

export interface Application {
  experimentName: string
  httpReferrer: string
  applicantField: Applicant[]
  generalDetailsField: GeneralDetails
  vehicleDetailsField: VehicleDetails | null
  itemsElementNameField: ['Source', 'Medium', 'Campaign']
  itemsField: string[]
  submittingPartyReferenceField: string | null
  quoteEventGuid: string | null
  optInMessageWording: string
  referralMessageWording: string
  marketingField?: { gCLIDField: string }
  applicationLeadIdSpecified?: boolean
  applicationLeadId?: number
  metadata: Record<string, string | undefined>
}

const retrieveAnswer = (
  answers: ApplicationFormAnswers,
  key: QuestionKey
) => answers[key]

const mapApplication = (
  answers: ApplicationFormAnswers,
  channelAttribution: Record<string, string>,
  experiment: string,
  marketingPreferences: MarketingPreferencesField,
  metadata: Metadata,
  vehicle?: VehicleDetailsDataObject,
  quoteId?: string,
  query?: ParsedUrlQuery
): Application => {
  const urlParams = query ?? queryString.parse(global.location.search)

  const { third_party_reference: thirdPartyRefCookie } = parseCookies()

  const calculatedSource = decodeURI(channelAttribution?.source || 'Direct')
  const calculatedMedium = decodeURI(channelAttribution?.medium || 'NoAttribution')
  const calculatedCampaign = decodeURI(channelAttribution?.campaign || 'Zuto')

  const getUrlParameter = (name: string): string => urlParams[name] as string

  const isCreditEngine = (source: string): boolean => source === 'creditengine' || source === 'credittechnologies'

  const getThirdPartyReferenceFromUrl = (): string => {
    if (isCreditEngine(calculatedSource)) return getUrlParameter('monevoId') || getUrlParameter('lead_ref')
    return getUrlParameter('lead_ref') || getUrlParameter('urn') || getUrlParameter('fbclid')
  }

  let leadRef = getThirdPartyReferenceFromUrl() || metadata.partnerCustomerId || thirdPartyRefCookie || metadata.customerId || null

  if (leadRef) {
    leadRef = leadRef.substring(0, 100)
  }
  const applicationLeadId = urlParams.al as string
  const personalDetailsAnswers = retrieveAnswer(
    answers,
    QuestionKey.PERSONAL_DETAILS
  ) as PersonalDetailsAnswers
  const title = retrieveAnswer(answers, QuestionKey.TITLE) as string
  const emailAnswers = retrieveAnswer(answers, QuestionKey.EMAIL_ADDRESS) as string
  const contactNumberAnswers = retrieveAnswer(answers, QuestionKey.CONTACT_NUMBER) as string
  const maritalStatusAnswers = retrieveAnswer(answers, QuestionKey.MARITAL_STATUS) as string
  const drivingLicenceAnswers = retrieveAnswer(
    answers,
    QuestionKey.DRIVING_LICENCE
  ) as string
  const addressAnswers = retrieveAnswer(answers, QuestionKey.ADDRESS_HISTORY) as AddressHistoryState
  const employmentAnswers = retrieveAnswer(
    answers,
    QuestionKey.EMPLOYMENT_HISTORY
  ) as EmploymentHistoryState
  const incomeAnswers = retrieveAnswer(answers, QuestionKey.MONTHLY_INCOME) as string
  const vehicleTypeAnswer = retrieveAnswer(answers, QuestionKey.VEHICLE_TYPE) as string
  const loanAmount = retrieveAnswer(answers, QuestionKey.LOAN_AMOUNT) as string

  let vehiclePrice = vehicle?.price || vehicle?.value || parseInt(loanAmount, 10)
  if (Number.isNaN(vehiclePrice)) {
    vehiclePrice = 7001
  }

  const application: Application = {
    experimentName: experiment,
    httpReferrer: query ? 'formlessJourney' : document.referrer,
    applicantField: [
      {
        basicDetailsField: {
          genderField: mapToGender(title),
          titleField: mapToTitle(title),
          forenameField: personalDetailsAnswers.firstName || '',
          surnameField: personalDetailsAnswers.surname || '',
          emailField: emailAnswers,
          phoneNumberField: [
            mapToPhone(contactNumberAnswers)
          ],
          dateOfBirthField: mapToDateOfBirth(personalDetailsAnswers)
        },
        additionalDetailsField: {
          maritalStatusField: mapToMaritalStatus(maritalStatusAnswers),
          licenceTypeField: mapToDrivingLicence(drivingLicenceAnswers),
          validUKPassportFieldSpecified: false
        },
        applicantAddressField: Object.keys(addressAnswers.entries)
          .map((key, index) => mapAddressEntryToFormAddress(addressAnswers.entries[key], index)),
        applicantEmploymentField: Object.keys(employmentAnswers.entries)
          .map((key, index) => mapToEmploymentForm(
            employmentAnswers.entries[key],
            parseInt(incomeAnswers, 10),
            addressAnswers.entries['1'].addressDetails?.town || 'UNKNOWN',
            index
          )),
        marketingPreferencesField: {
          phoneField: true,
          emailField: marketingPreferences.email,
          smsField: marketingPreferences.sms,
          thirdPartyReferralField: false,
          thirdPartyReferralFieldSpecified: true
        }
      }
    ],
    generalDetailsField: {
      creditLimitField: vehiclePrice,
      vehicleTypeField: mapToVehicleType(vehicle?.type || vehicle?.vehicleType || vehicleTypeAnswer)
    },
    vehicleDetailsField: mapToVehicleDetails(vehiclePrice, vehicle),
    itemsElementNameField: ['Source', 'Medium', 'Campaign'],
    itemsField: [calculatedSource, calculatedMedium, calculatedCampaign],
    submittingPartyReferenceField: leadRef,
    quoteEventGuid: quoteId || null,
    optInMessageWording: marketingPreferences.consentText,
    referralMessageWording: '',
    metadata: {
      customerId: metadata.customerId || undefined,
      partnerCustomerId: metadata.partnerCustomerId || undefined,
      deviceId: metadata.deviceId,
      sessionId: metadata.sessionId,
      lender: metadata.lender || undefined,
      isBotTraffic: metadata.isBotTraffic ? '1' : '0',
      isOfficeTraffic: metadata.isOfficeTraffic ? '1' : '0',
      isRetry: metadata.isRetry !== undefined ? `${metadata.isRetry ? '1' : '0'}` : undefined,
      isPreapproved: metadata.isPreapproved !== undefined ? `${metadata.isPreapproved ? '1' : '0'}` : undefined,
      isPrefill: metadata.isPrefill !== undefined ? `${metadata.isPrefill ? '1' : '0'}` : undefined,
      experiments: metadata.experiments,
      journey: metadata.journey,
      wbraid: metadata.wbraid,
      gbraid: metadata.gbraid,
      fbclid: metadata.fbclid
    }
  }

  const queryAdvertId = getUrlParameter('advertId') || getUrlParameter('advertid')
  if (queryAdvertId) {
    application.metadata.advertIdFromQuery = queryAdvertId
  }

  if (channelAttribution && Object.keys(channelAttribution).length > 0) {
    application.marketingField = {
      gCLIDField: channelAttribution.gclid
    }
  }

  if (applicationLeadId) {
    application.applicationLeadId = parseInt(applicationLeadId, 10)
    application.applicationLeadIdSpecified = true
  }

  return application
}

export default mapApplication
